import {
  CheckCircleFilled,
  ClockCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  cubicMeters,
  FoundRecordCard,
  MonthSwitch,
  metersCount,
} from '@horof/shared';
import {
  Alert,
  Button,
  Empty,
  Input,
  Progress,
  Segmented,
  Skeleton,
} from 'antd';
import { Link } from 'react-router';
import { matchesSearch, type RoundMeter, useRound } from './round';
import { type ListFilter, useRoundState } from './round-state';

export function RoundPage() {
  const { month, setMonth, search, setSearch, filter, setFilter } =
    useRoundState();
  const { meters, readCount, loaded, switching, error, refetch } =
    useRound(month);

  const total = meters.length;
  const unreadCount = total - readCount;
  const visible = meters.filter(
    (m) =>
      (filter === 'all' || (filter === 'read') === Boolean(m.bill)) &&
      matchesSearch(m, search),
  );

  return (
    <div className="page">
      <header className="round-header">
        <MonthSwitch month={month} onChange={setMonth} loading={switching} />

        {loaded && (
          <div className={`progress${switching ? ' is-switching' : ''}`}>
            <div className="progress-count ltr-num">
              {readCount} / {total}
            </div>
            <Progress
              percent={total ? Math.round((readCount / total) * 100) : 0}
              showInfo={false}
              strokeColor="#1a8f3c"
              size={['100%', 14]}
            />
            <div className={unreadCount ? 'progress-left' : 'progress-done'}>
              {unreadCount
                ? `بقي ${metersCount(unreadCount)} بدون قراءة`
                : 'قُرئت كل العدادات هذا الشهر'}
            </div>
          </div>
        )}

        <Input
          size="large"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="بحث: الاسم، رقم العداد، العنوان"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Segmented<ListFilter>
          block
          size="large"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'unread', label: `لم تُقرأ (${unreadCount})` },
            { value: 'read', label: `قُرئت (${readCount})` },
            { value: 'all', label: `الكل (${total})` },
          ]}
        />
      </header>
      <FoundRecordCard
        text={search}
        href={(f) => (f.meterId ? `/meter/${f.meterId}` : null)}
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message="تعذّر تحميل العدادات"
          description="تحقّق من الاتصال بالإنترنت."
          action={
            <Button onClick={() => refetch({ requestPolicy: 'network-only' })}>
              أعد المحاولة
            </Button>
          }
        />
      )}

      {!loaded && !error && <Skeleton active paragraph={{ rows: 8 }} />}

      {loaded && (
        <ol className={`meter-list${switching ? ' is-switching' : ''}`}>
          {visible.map((meter) => (
            <li key={meter.id}>
              <MeterRow meter={meter} />
            </li>
          ))}
        </ol>
      )}
      {loaded && visible.length === 0 && (
        <Empty
          description={
            search
              ? 'لا يوجد عداد بهذا البحث'
              : filter === 'unread'
                ? 'لا يوجد عداد بدون قراءة'
                : 'لا توجد عدادات'
          }
        />
      )}
    </div>
  );
}

function MeterRow({ meter }: { meter: RoundMeter }) {
  return (
    <Link
      to={`/meter/${meter.id}`}
      className={`meter-row ${meter.bill ? 'is-read' : 'is-unread'}`}
    >
      <span className="meter-position ltr-num">{meter.position}</span>
      <span className="meter-main">
        <span className="meter-member">{meter.memberName || meter.label}</span>
        <span className="meter-sub">
          العداد {meter.label}
          {meter.address && meter.address !== meter.label
            ? ` · ${meter.address}`
            : ''}
        </span>
      </span>
      <span className="meter-status">
        {meter.bill ? (
          <>
            <CheckCircleFilled />
            <span>{cubicMeters(meter.bill.consumption)}</span>
          </>
        ) : (
          <>
            <ClockCircleOutlined />
            <span>لم يُقرأ</span>
          </>
        )}
      </span>
    </Link>
  );
}
